import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Loader2,
  RotateCcw,
  Rocket,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import {
  useDeploymentSummary,
  useRunPreflight,
  useRunDryRun,
  useStartPush,
  useForceAwaitingConfirmation,
  useConfirmDeployment,
  useCancelDeployment,
} from '../hooks/useDeployments';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { DeploymentLogView } from '../components/deployments/DeploymentLogView';
import { VerificationChecklist } from '../components/deployments/VerificationChecklist';
import { getApiErrorMessage } from '../api/client';
import { formatRelativeTime } from '../utils/format';
import { cn } from '../utils/cn';
import type { DeploymentRecord } from '../types';

const outcomeTone: Record<NonNullable<DeploymentRecord['outcome']>, 'success' | 'warning' | 'neutral' | 'danger'> = {
  success: 'success',
  rolled_back: 'warning',
  aborted: 'neutral',
  failed: 'danger',
};

const outcomeLabel: Record<NonNullable<DeploymentRecord['outcome']>, string> = {
  success: 'Completed',
  rolled_back: 'Rolled back',
  aborted: 'Cancelled',
  failed: 'Failed',
};

function PreChecklistGate({ onProceed, pending }: { onProceed: () => void; pending: boolean }) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const items = [
    'Content changes are finished and checked on DEV',
    'You are working outside teaching hours where possible',
    "You can stay at the keyboard for the full push - it pauses partway through and waits for your decision",
  ];
  const allChecked = items.every((_, i) => checked[i]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Before you start - confirm each of these, straight from the SOP&apos;s &quot;Before You Start&quot; section:
      </p>
      <div className="space-y-2">
        {items.map((item, i) => (
          <label key={i} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={checked[i] ?? false}
              onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-pulse-600 focus:ring-pulse-500"
            />
            {item}
          </label>
        ))}
      </div>
      <Button onClick={onProceed} disabled={!allChecked || pending}>
        {pending && <Loader2 size={14} className="animate-spin" />}
        Run preflight checks
      </Button>
    </div>
  );
}

function HistoryRow({ record }: { record: DeploymentRecord }) {
  const [open, setOpen] = useState(false);
  const durationMin = record.completedAt
    ? Math.round((new Date(record.completedAt).getTime() - new Date(record.startedAt).getTime()) / 60_000)
    : null;

  return (
    <div className="rounded-lg border border-slate-100 dark:border-slate-800">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 p-3 text-left">
        {record.outcome && <Badge tone={outcomeTone[record.outcome]}>{outcomeLabel[record.outcome]}</Badge>}
        <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200">
          Started by {record.startedBy.name} &middot; {formatRelativeTime(record.startedAt)}
        </span>
        {durationMin !== null && <span className="hidden shrink-0 text-xs text-slate-400 sm:inline">{durationMin} min</span>}
        <ChevronDown size={16} className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 p-3 dark:border-slate-800">
          {record.errorMessage && <p className="text-sm text-rose-600 dark:text-rose-400">{record.errorMessage}</p>}
          {record.reviewedByEmail && (
            <p className="text-xs text-slate-400">Confirmation reviewed by {record.reviewedByEmail}</p>
          )}
          <DeploymentLogView log={record.log} maxHeight={220} />
        </div>
      )}
    </div>
  );
}

export function Deployments() {
  const { data, isLoading } = useDeploymentSummary();
  const preflight = useRunPreflight();
  const dryRun = useRunDryRun();
  const push = useStartPush();
  const forceConfirm = useForceAwaitingConfirmation();
  const confirmMutation = useConfirmDeployment();
  const cancelMutation = useCancelDeployment();
  const [reviewed, setReviewed] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (!data.configured) {
    return (
      <Card className="flex flex-col items-center gap-3 p-14 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-400 dark:bg-slate-800">
          <Rocket size={22} />
        </div>
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Deployments</h2>
        <p className="max-w-md text-sm text-slate-400">
          DEV → LIVE deployment control for {data.site} isn&apos;t configured yet. This SSHes into the Hostinger box
          and runs <code className="rounded bg-slate-100 px-1 py-0.5 dark:bg-slate-800">push_dev_to_live.sh</code> -
          set <code className="rounded bg-slate-100 px-1 py-0.5 dark:bg-slate-800">DEPLOY_SSH_*</code> in
          backend/.env to turn this on. See the README &quot;Deployments&quot; section for how to scope a dedicated,
          restricted SSH key for this before enabling it.
        </p>
      </Card>
    );
  }

  const active = data.active;
  const mutationError = [preflight.error, dryRun.error, push.error, confirmMutation.error, cancelMutation.error, forceConfirm.error].find(
    Boolean,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Deployments</h1>
          <p className="text-sm text-slate-400">
            DEV → LIVE for{' '}
            <a href={data.siteUrl} target="_blank" rel="noreferrer" className="text-pulse-600 hover:underline dark:text-pulse-400">
              {data.site}
            </a>{' '}
            only.
          </p>
        </div>
      </div>

      {mutationError && (
        <Card className="border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-300">
          {getApiErrorMessage(mutationError)}
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {active ? `In progress - started by ${active.startedBy.name}` : 'Start a deployment'}
          </CardTitle>
          {active && <span className="text-xs text-slate-400">{formatRelativeTime(active.startedAt)}</span>}
        </CardHeader>
        <CardBody className="space-y-4">
          {!active && <PreChecklistGate onProceed={() => preflight.mutate()} pending={preflight.isPending} />}

          {active?.phase === 'preflight_running' && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 size={16} className="animate-spin" /> Running preflight checks - read-only, changes nothing...
            </div>
          )}

          {active?.phase === 'preflight_complete' && (
            <div className="space-y-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-pulse-700 dark:text-pulse-400">
                <CheckCircle2 size={16} /> Preflight passed. Review the output below, then run a dry run.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => dryRun.mutate()} disabled={dryRun.isPending}>
                  {dryRun.isPending && <Loader2 size={14} className="animate-spin" />}
                  Run dry run
                </Button>
                <Button variant="secondary" onClick={() => cancelMutation.mutate({ id: active.id })} disabled={cancelMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {active?.phase === 'preflight_failed' && (
            <p className="flex items-center gap-1.5 text-sm font-medium text-rose-600 dark:text-rose-400">
              <XCircle size={16} /> Preflight failed - see the log below. This session has ended; check the history table once it clears.
            </p>
          )}

          {active?.phase === 'dry_run_running' && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 size={16} className="animate-spin" /> Running the dry run - walking through the deployment without writing anything...
            </div>
          )}

          {active?.phase === 'dry_run_complete' && (
            <div className="space-y-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-pulse-700 dark:text-pulse-400">
                <CheckCircle2 size={16} /> Dry run passed with no errors.
              </p>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
                <ShieldAlert size={14} className="mr-1.5 inline" />
                Starting the real push changes the live site students are using. Don&apos;t start it and walk away -
                it pauses partway through and waits for your decision.
              </div>
              <div className="flex gap-2">
                <Button onClick={() => push.mutate()} disabled={push.isPending}>
                  {push.isPending && <Loader2 size={14} className="animate-spin" />}
                  Start real push
                </Button>
                <Button variant="secondary" onClick={() => cancelMutation.mutate({ id: active.id })} disabled={cancelMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {active?.phase === 'dry_run_failed' && (
            <p className="flex items-center gap-1.5 text-sm font-medium text-rose-600 dark:text-rose-400">
              <XCircle size={16} /> Dry run failed - see the log below. This session has ended; check the history table once it clears.
            </p>
          )}

          {active?.phase === 'push_running' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <Loader2 size={16} className="animate-spin" /> Push running - the file backup can take several minutes and can look
                like nothing is happening. That&apos;s normal.
              </div>
              <button
                onClick={() => forceConfirm.mutate()}
                disabled={forceConfirm.isPending}
                className="text-xs text-slate-400 underline decoration-dotted hover:text-slate-600 dark:hover:text-slate-300"
              >
                Log below already shows it&apos;s waiting for a decision? Force show confirmation controls.
              </button>
            </div>
          )}

          {active?.phase === 'awaiting_confirmation' && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                The script is waiting for a decision. Review every line below before deciding - per the SOP, if a
                blocking check is genuinely missing, the answer is abort.
              </p>
              <VerificationChecklist checks={active.verificationChecks} />
              <label className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-pulse-600 focus:ring-pulse-500"
                />
                I have reviewed the checklist and the raw output above
              </label>
              <div className="flex gap-2">
                <Button
                  onClick={() => confirmMutation.mutate({ id: active.id, decision: 'confirm' })}
                  disabled={!reviewed || confirmMutation.isPending}
                >
                  {confirmMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Confirm deployment
                </Button>
                <Button
                  variant="danger"
                  onClick={() => confirmMutation.mutate({ id: active.id, decision: 'abort' })}
                  disabled={!reviewed || confirmMutation.isPending}
                >
                  Abort / roll back
                </Button>
              </div>
            </div>
          )}

          {(active?.phase === 'confirming' || active?.phase === 'rolling_back') && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 size={16} className="animate-spin" /> {active.phase === 'confirming' ? 'Finishing the deployment...' : 'Rolling back...'}
            </div>
          )}

          {active?.phase === 'completed' && (
            <div className="space-y-2 rounded-lg border border-pulse-200 bg-pulse-50 p-3 text-sm dark:border-pulse-900/40 dark:bg-pulse-900/10">
              <p className="flex items-center gap-1.5 font-medium text-pulse-700 dark:text-pulse-400">
                <CheckCircle2 size={16} /> Deployment completed.
              </p>
              <p className="text-pulse-700/80 dark:text-pulse-400/80">
                Last step (SOP #7): open the site and confirm courses load, a student can log in, groups show their
                seat numbers, and the menu is complete.
              </p>
              <a
                href={data.siteUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-pulse-700 hover:underline dark:text-pulse-400"
              >
                Open {data.site} <ExternalLink size={12} />
              </a>
            </div>
          )}

          {active?.phase === 'rolled_back' && (
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400">
              <RotateCcw size={16} /> Rolled back - LIVE was not changed.
            </p>
          )}

          {active?.phase === 'aborted' && (
            <p className="flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">
              <XCircle size={16} /> Cancelled before the real push started - nothing was written to LIVE.
            </p>
          )}

          {active?.phase === 'failed' && (
            <p className="flex items-center gap-1.5 text-sm font-medium text-rose-600 dark:text-rose-400">
              <AlertTriangle size={16} /> {active.errorMessage ?? 'Deployment failed.'}
            </p>
          )}

          {active && active.log.length > 0 && active.phase !== 'awaiting_confirmation' && (
            <DeploymentLogView log={active.log} />
          )}
        </CardBody>
      </Card>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-500 dark:text-slate-400">Deployment history</h2>
        {data.history.length === 0 ? (
          <p className="text-sm text-slate-400">No deployments recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {data.history.map((record) => (
              <HistoryRow key={record.id} record={record} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
